import { useStore as useStore$1, connect, useDispatch, Provider } from 'react-redux';
export { connect, useDispatch, useSelector } from 'react-redux';
import { createStore, applyMiddleware, combineReducers } from 'redux';
export { combineReducers } from 'redux';
import { PersistGate } from 'redux-persist/integration/react';
import * as actions from 'ts-action';
export { actions };
import { useLocalStorage, parseHtml, DistanceUnit } from '@grandvisionhq/utils';
export { DistanceUnit } from '@grandvisionhq/utils';
import * as React from 'react';
import React__default, { useContext } from 'react';
import * as swcHelpers from '@swc/helpers';
import { composeWithDevTools } from 'redux-devtools-extension';
import { composeWithDevTools as composeWithDevTools$1 } from 'redux-devtools-extension/logOnly';
import { persistStore, persistReducer as persistReducer$1 } from 'redux-persist';
import sessionStorage from 'redux-persist/lib/storage/session';
export { default as sessionStorage } from 'redux-persist/lib/storage/session';
import createSagaMiddleware, { END } from 'redux-saga';
export { default as localStorage } from 'redux-persist/lib/storage';
import { createSelector } from 'reselect';
import { format } from 'date-fns';

const g = typeof window === 'undefined' ? global : window;
function singletonMap(name) {
    const key = `__gv_singleton_maps-${name}`;
    if (key in g) {
        return g[key];
    }
    return g[key] = {};
}

const sagasMap = singletonMap('sagas');
let listener$1;
const SagaRegistry = {
    register (name, saga) {
        if (name in sagasMap) {
            console.warn(`Saga with name '${name}' is already registered`);
            return;
        }
        sagasMap[name] = saga;
        if (listener$1) {
            listener$1(saga);
        }
    },
    set listener (l){
        listener$1 = l;
    },
    get sagas () {
        return Object.values(sagasMap);
    }
};

const reducersMap = singletonMap('reducers');
let listener;
const ReducersRegistry = {
    get reducers () {
        return swcHelpers.objectSpread({}, reducersMap);
    },
    set listener (l){
        listener = l;
    },
    register (name, reducer) {
        reducersMap[name] = reducer;
        if (listener) {
            listener(swcHelpers.objectSpread({}, reducersMap));
        }
    }
};

const sagaMiddleware = createSagaMiddleware();
const bindMiddleware = (middleware)=>{
    return applyMiddleware(...middleware);
};
const persistConfig = {
    key: 'grandvision_store',
    whitelist: [
        'checkout',
        'cart'
    ],
    storage: sessionStorage
};
const getRootReducer = (reducers, isServer)=>{
    const rootReducer = combineReducers(swcHelpers.objectSpread({}, reducers));
    if (isServer) {
        return rootReducer;
    }
    return persistReducer$1(persistConfig, rootReducer);
};
const initStore = (initialState = {}, { isServer  })=>{
    const rootReducer = getRootReducer(ReducersRegistry.reducers, isServer);
    const devToolsOptions = {
        serialize: true
    };
    const devTools = process.env.NODE_ENV !== 'production' ? composeWithDevTools(devToolsOptions) : composeWithDevTools$1(devToolsOptions);
    const plainStore = createStore(rootReducer, initialState, devTools(bindMiddleware([
        sagaMiddleware
    ])));
    // Add the redux persistor to the store
    const persistor = persistStore(plainStore);
    let store;
    if (!isServer) {
        store = Object.assign(plainStore, {
            persistor,
            // Make sure we run the sagas that have been registered before the store is initialized
            runningSagas: SagaRegistry.sagas.map(sagaMiddleware.run),
            runSaga: sagaMiddleware.run,
            close: swcHelpers.asyncToGenerator(function*() {
                store.dispatch(END);
                yield Promise.all(store.runningSagas.map((t)=>t.toPromise()
                ));
            })
        });
    } else {
        store = Object.assign(plainStore, {
            persistor,
            runningSagas: [],
            close: ()=>Promise.resolve()
        });
        SagaRegistry.listener = (sagas)=>{
            store.runningSagas.push(sagaMiddleware.run(sagas));
        };
    }
    ReducersRegistry.listener = (reducers)=>{
        store.replaceReducer(getRootReducer(reducers, isServer));
    };
    return store;
};
function useStore() {
    return useStore$1();
}

const useSaga = (saga, options, deps = [])=>{
    const store = useStore();
    const [task, setTask] = React.useState(null);
    React.useDebugValue(task, (t)=>t && {
            isRunning: t.isRunning(),
            error: t.error(),
            result: t.result()
        }
    );
    React.useEffect(()=>{
        const runningSaga = store.runSaga(saga);
        setTask(runningSaga);
        if (options === null || options === void 0 ? void 0 : options.onStart) {
            options === null || options === void 0 ? void 0 : options.onStart(runningSaga);
        }
        return ()=>{
            runningSaga.cancel();
        };
    // options.onStart is left out on purpose
    // putting in would cause infinite loops
    }, [
        store,
        saga,
        ...deps
    ]);
    return task;
};

const DEFAULT_LIMIT = 20;
/**
 * Creates a history array in the local storage with some helpers attached.
 *
 * @param key - used as key in the local storage
 * @param limit - limit of items stored in the history array
 *
 * @example usage
 *  import { useViewHistory } from '@grandvisionhq/state'
 *  export const useProductViewHistory = useViewHistory.bind(null, ViewHistoryKey.Products, 30)
 */ function useViewHistory(key, limit = DEFAULT_LIMIT) {
    const [history, setHistory] = useLocalStorage(key, undefined);
    /**
   * Checks if an id exists in the current history
   */ const has = React.useCallback((id)=>{
        var ref;
        return Boolean(history && ((ref = history.find((i)=>i === id
        )) === null || ref === void 0 ? void 0 : ref.length));
    }, [
        history
    ]);
    /**
   * Adds one ID to the history array.
   * Duplicated IDs are not allowed.
   * New items are always added at the start of the history.
   * If the limit is exceeded, the last item(s) of the array are cut off.
   */ const add = React.useCallback((id)=>{
        setHistory((state = [])=>{
            var ref;
            const hasId = Boolean((ref = state.find((i)=>i === id
            )) === null || ref === void 0 ? void 0 : ref.length);
            const filtered = hasId ? [
                ...state.filter((i)=>i !== id
                )
            ] : [
                ...state
            ];
            return [
                id,
                ...filtered
            ].slice(0, limit);
        });
    }, [
        limit
    ]);
    /**
   * Removes one or more items from the history
   */ const remove = React.useCallback((ids)=>{
        setHistory((state = [])=>{
            const filters = Array.isArray(ids) ? ids : [
                ids
            ];
            return filters.reduce((result, next)=>[
                    ...result.filter((i)=>i !== next
                    )
                ]
            , [
                ...state
            ]);
        });
    }, []);
    /**
   * Clears the entire history array by restoring the initial value
   */ const clear = React.useCallback(()=>{
        setHistory([]);
    }, []);
    return {
        history,
        has,
        add,
        remove,
        clear
    };
}

const persistReducer = (config, reducer)=>{
    if (typeof window !== 'undefined' && window.document) {
        return persistReducer$1(config, reducer);
    }
    return reducer;
};

const INIT = 'api-config/INIT';
const init = (payload)=>({
        type: INIT,
        payload
    })
;

function reducer$3(state = null, action) {
    switch(action.type){
        case INIT:
            return swcHelpers.objectSpread({}, state, action.payload);
        default:
            return state;
    }
}

const getApiConfig = (state)=>{
    return state.apiConfig;
};

var selectors$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getApiConfig: getApiConfig
});

const createApiConfig = (config)=>({
        apiUrl: config.apiUrl,
        cartsUrl: config.cartsUrl || `${config.apiUrl}/carts`,
        locationsUrl: config.locationsUrl || `${config.apiUrl}/locations`,
        ordersUrl: config.ordersUrl || `${config.apiUrl}/orders`,
        paymentsUrl: config.paymentsUrl || `${config.apiUrl}/payments`,
        productsUrl: config.productsUrl || `${config.apiUrl}/products`,
        storesUrl: config.storesUrl || `${config.apiUrl}/stores`,
        subscriptionsUrl: config.subscriptionsUrl || `${config.apiUrl}/subscriptions`
    })
;

const ApiConfigContext = /*#__PURE__*/ React.createContext(null);
function assertApiConfig(apiConfig) {
    if (apiConfig === null || apiConfig === undefined) {
        throw new Error('Missing API config provider. You have to wrap the application with the ApiConfigProvider component from the @grandvisionhq/state package.');
    }
}
const ApiConfigProvider = (props)=>{
    React.useEffect(()=>{
        props.init(createApiConfig(props.apiConfig));
    }, [
        JSON.stringify(props.apiConfig)
    ]);
    return /*#__PURE__*/ React.createElement(ApiConfigContext.Provider, {
        value: createApiConfig(props.apiConfig)
    }, props.children);
};
function withApiConfig(Component) {
    const wrappedName = Component.displayName || Component.name || 'Component';
    const WithApiConfig = (props)=>/*#__PURE__*/ React.createElement(ApiConfigContext.Consumer, null, (apiConfig)=>{
            assertApiConfig(apiConfig);
            return /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, props, {
                apiConfig: apiConfig
            }));
        })
    ;
    WithApiConfig.displayName = `WithApiConfig(${wrappedName})`;
    return WithApiConfig;
}
function useApiConfig() {
    const context = React.useContext(ApiConfigContext);
    if (context === null) {
        throw new Error('Missing API config provider. You have to wrap the application with the ApiConfigProvider component from the @grandvisionhq/state package.');
    }
    return context;
}
const mapDispatchToProps = {
    init
};
var ApiConfig = connect(null, mapDispatchToProps)(ApiConfigProvider);

ReducersRegistry.register('apiConfig', reducer$3);

const SET_FEATURE_TOGGLES = 'feature-toggles/SET';
const setFeatureToggles = (payload)=>({
        type: SET_FEATURE_TOGGLES,
        payload
    })
;

function reducer$2(state = null, action) {
    switch(action.type){
        case SET_FEATURE_TOGGLES:
            return swcHelpers.objectSpread({}, state, action.payload);
        default:
            return state;
    }
}

const getFeatureToggles = (state)=>state.featureToggles
;

var selectors$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getFeatureToggles: getFeatureToggles
});

const FeatureToggleContext = /*#__PURE__*/ React.createContext({});
const useFeatureToggles = ()=>React.useContext(FeatureToggleContext)
;
function withFeatureToggles(Component) {
    const wrappedName = Component.displayName || Component.name || 'Component';
    const WithFeatureToggles = (props)=>/*#__PURE__*/ React.createElement(FeatureToggleContext.Consumer, null, (context)=>/*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, props, context))
        )
    ;
    WithFeatureToggles.displayName = `WithFeatureToggles(${wrappedName})`;
    return WithFeatureToggles;
}
const FeatureToggleProvider = ({ children , featureToggles  })=>{
    const dispatch = useDispatch();
    const value = React.useMemo(()=>featureToggles
    , [
        featureToggles
    ]);
    React.useEffect(()=>{
        // Init/update feature toggle settings to our redux state
        dispatch(setFeatureToggles(value));
    }, [
        dispatch,
        value
    ]);
    return /*#__PURE__*/ React.createElement(FeatureToggleContext.Provider, {
        value: value
    }, children);
};

var ClickAndReserve;
(function(ClickAndReserve) {
    ClickAndReserve["STOCK"] = "STOCK";
    ClickAndReserve["STOCK_RESERVATION"] = "STOCK_RESERVATION";
    ClickAndReserve["STOCK_RESERVATION_APPOINTMENT"] = "STOCK_RESERVATION_APPOINTMENT";
})(ClickAndReserve || (ClickAndReserve = {}));

function assertClickAndReserveFeatureConfig(value) {
    if (value && !(value in ClickAndReserve)) {
        throw new Error('environment variable `FEATURE_CONFIGURATION_CLICK_AND_RESERVE` does not contain a valid value');
    }
}
const getClickAndReserveFeatureConfig = (value)=>{
    assertClickAndReserveFeatureConfig(value);
    return value;
};

const featureToggleLookup = {
    AAB: 'isAABEnabled',
    AAB_ALTERNATIVE_STORE_AVAILABILITY: 'isAabAlternativeStoreAvailabilityEnabled',
    ABTESTING: 'isABTestingEnabled',
    ADDRESSBOOK: 'isAddressBookEnabled',
    APPOINTMENT_BOOKING_TYPE_WITH_FREE_INDICATION: 'isAppointmentBookingTypeWithFreeIndication',
    BYNDER_IMAGES: 'isBynderImagesEnabled',
    CONTACTLENS_STOCK: 'isContactLensStockEnabled',
    CONTACTLENS: 'isContactLensEnabled',
    CART_DISCOUNTS: 'isCartDiscountsEnabled',
    CROSS_SELL: 'isCrossSellEnabled',
    DOWNLOAD_QUOTATION: 'isDownloadQuotationEnabled',
    EYE_EXAM: 'isEyeExamEnabled',
    GPS_STORES: 'isGpsStoresEnabled',
    HIDE_USER_DATA_FROM_ANALYTICS: 'isHideUserDataFromAnalyticsEnabled',
    LEGACY_ORDER_SUPPLIER_EAN: 'isLegacyOrderSupplierEanEnabled',
    MONETATE_PRODUCT_DATA: 'isMonetateProductDataEnabled',
    MULTI_COUNTRY_LOCATIONS: 'isMultiCountryLocationsEnabled',
    MULTILINGUAL: 'isMultilingualEnabled',
    MYACCOUNT: 'isMyAccountEnabled',
    OLAPIC_PROVIDER: 'isOlapicProviderEnabled',
    OMNI_VOUCHER: 'isOmniVoucherEnabled',
    PRODUCT_LABEL: 'isProductLabelEnabled',
    PERSISTENT_QUERY_DISABLED: 'isPersistentQueryDisabled',
    PRESCRIPTION_FILE: 'isPrescriptionFileEnabled',
    PRESCRIPTION_RECOGNITION: 'isPrescriptionRecognitionEnabled',
    PRESCRIPTION_GLASSES_CONFIGURATOR_VALIDATION_STEP: 'isPrescriptionGlassesConfiguratorValidationStepEnabled',
    PRESCRIPTION_GLASSES_REORDER: 'isPrescriptionGlassesReorderEnabled',
    PRESCRIPTION_GLASSES: 'isPrescriptionGlassesEnabled',
    REVIEWS: 'isReviewsEnabled',
    REORDER_BUTTON: 'isReorderButtonEnabled',
    STORE_SEARCH_SHOW_EMAIL: 'isStoreSearchEmailEnabled',
    STORE_SEARCH_SHOW_PHONE: 'isStoreSearchPhoneEnabled',
    STORE_DETAILS_SHOW_EMAIL: 'isStoreDetailsEmailEnabled',
    STORE_DETAILS_SHOW_PHONE: 'isStoreDetailsPhoneEnabled',
    STORE_PICKUP_SHOW_EMAIL: 'isStorePickupEmailEnabled',
    STORE_PICKUP_SHOW_PHONE: 'isStorePickupPhoneEnabled',
    STORE_RATINGS: 'isStoreRatingsEnabled',
    STORES_NEARBY: 'isStoresNearbyEnabled',
    STORES_PLACE_PREDICTIONS: 'isStoresPlacePredictionsEnabled',
    STORES_DISABLE_AAB_STORE_VALUE_AAB_BUTTON: 'isStoreValueInAabButtonDisabled',
    DELIVERY_FULL_DATE: 'isDeliveryFullDateEnabled',
    CART_RECOMMENDATIONS: 'isCartRecommendationsEnabled',
    VTO: 'isVtoEnabled',
    FRAME_SIZE_VTO: 'isFrameSizeVTOEnabled',
    APPLE_PAY_EXPRESS_CHECKOUT: 'isApplePayExpressCheckoutEnabled',
    PAYMENT_CALLBACK_API_ROUTE: 'isPaymentCallbackApiRouteEnabled',
    FEATURE_CHECKOUT_PAYMENT_PAGE_ENABLED: 'isCheckoutPaymentPageEnabled',
    CLICK_AND_RESERVE: 'isClickAndReserveEnabled',
    PAYMENT_METHODS_NEW_UI: 'isPaymentMethodsNewUIEnabled',
    FEATURE_ENABLE_SOCIAL_LOGIN_FOR_CHECKOUT: 'isSocialLoginForCheckoutEnabled'
};
const createFeatureSet = (enabledEnvFeatures)=>{
    const enabledFeaturesSet = new Set(enabledEnvFeatures);
    const completeFeatureSet = {};
    Object.entries(featureToggleLookup).forEach(([key, mappedKey])=>{
        completeFeatureSet[mappedKey] = enabledFeaturesSet.has(key);
    });
    return completeFeatureSet;
};
/**
 * This function parses comma separated strings to arrays via the regular `split()`.
 * The benefit of this function is that it catches errors and removes empty values.
 */ const parseCommaSeparatedString = (string)=>{
    try {
        const array = string === null || string === void 0 ? void 0 : string.split(',') // Split comma separated string
        .map((value)=>value.trim()
        ).filter((value)=>value
        ) // Remove empty values
        ;
        return (array === null || array === void 0 ? void 0 : array.length) ? array : undefined;
    } catch (error) {
        console.error(`Error parsing feature config string '${string}': ${error}`);
    }
};
const parseIntBaseTen = (value)=>value !== undefined ? parseInt(value, 10) : undefined
;
var _SURVEY_LINK;
const createFeatureConfigs = (configs)=>{
    var ref, ref1;
    return {
        eyeExamConsentKey: configs.EYE_EXAM_CONSENT_KEY || 'eye-exam',
        prescriptionTypesOrder: parseCommaSeparatedString(configs.PRESCRIPTION_TYPES_ORDER),
        prescriptionList: parseCommaSeparatedString(configs.PRESCRIPTION_LIST),
        configuratorUspsGroupTheme: configs.CONFIGURATOR_USPS_GROUP_THEME || 'light',
        maxSearchStoresResults: parseIntBaseTen(configs.MAX_SEARCH_STORES_RESULTS),
        maxNearbyStoresDistance: parseIntBaseTen(configs.MAX_NEARBY_STORES_DISTANCE_METERS),
        appointmentBookingUrlPaddedLength: parseIntBaseTen(configs.APPOINTMENT_BOOKING_URL_PADDED_LENGTH),
        appointmentBookingAvailabiltyCalendarWeekCount: parseIntBaseTen(configs.APPOINTMENT_BOOKING_AVAILABILITY_CALENDAR_WEEK_COUNT),
        appointmentBookingVirtualAppointmentTypeId: configs === null || configs === void 0 ? void 0 : configs.APPOINTMENT_BOOKING_VIRTUAL_APPOINTMENT_TYPE_ID,
        appointmentBookingVirtualAppointmentStoreCode: parseIntBaseTen(configs.APPOINTMENT_BOOKING_VIRTUAL_APPOINTMENT_STORE_CODE),
        appointmentBookingSurveyLinks: configs.APPOINTMENT_BOOKING_SURVEY_LINKS !== undefined ? Array.from(configs.APPOINTMENT_BOOKING_SURVEY_LINKS.matchAll(/([a-zA-Z-0-9]+)>([^\^]+)/g), ([, appointmentId, url])=>[
                appointmentId,
                url
            ]
        ) : undefined,
        appointmentBookingSurveyLinksStoreCodes: parseCommaSeparatedString(configs.APPOINTMENT_BOOKING_SURVEY_LINKS_STORE_CODES),
        surveyLink: (_SURVEY_LINK = configs.SURVEY_LINK) !== null && _SURVEY_LINK !== void 0 ? _SURVEY_LINK : undefined,
        appointmentBookingAlternativeUrl: configs.APPOINTMENT_BOOKING_ALTERNATIVE_URL,
        appointmentBookingAlternativeUrlStores: (ref = parseCommaSeparatedString(configs.APPOINTMENT_BOOKING_ALTERNATIVE_URL_STORES)) === null || ref === void 0 ? void 0 : ref.map((string)=>parseInt(string, 10)
        ),
        storeDistanceConfig: (ref1 = parseCommaSeparatedString(configs.STORE_DISTANCE_CONFIG)) === null || ref1 === void 0 ? void 0 : ref1.reduce((indexed, distanceProp)=>{
            indexed[distanceProp] = true;
            return indexed;
        }, {}),
        googleDynamicMapsMapId: configs.GOOGLE_DYNAMIC_MAPS_MAP_ID,
        clickAndReserve: getClickAndReserveFeatureConfig(configs.CLICK_AND_RESERVE),
        additionalLoginProviders: parseCommaSeparatedString(configs.ADDITIONAL_LOGIN_PROVIDERS),
        virtualTryOn: configs.VIRTUAL_TRY_ON || 'DITTO'
    };
};

ReducersRegistry.register('featureToggles', reducer$2);

const useClickAndReserveFeatureConfig = ()=>{
    const { featureConfigs , isClickAndReserveEnabled  } = useFeatureToggles();
    if (isClickAndReserveEnabled) {
        if ((featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.clickAndReserve) === ClickAndReserve.STOCK) {
            return 'stock_only';
        } else if ((featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.clickAndReserve) === ClickAndReserve.STOCK_RESERVATION) {
            return 'reservation';
        }
    }
};

const SET_CONFIG = 'intl/SET_CONFIG';
const setConfig = (payload)=>({
        type: SET_CONFIG,
        payload
    })
;

function reducer$1(state = null, action) {
    switch(action.type){
        case SET_CONFIG:
            return swcHelpers.objectSpread({}, state, action.payload);
        default:
            return state;
    }
}

const getIntl = (state)=>state.intl
;
var ref;
const getCurrency = createSelector(getIntl, (intl)=>{
    return (ref = intl === null || intl === void 0 ? void 0 : intl.currency) !== null && ref !== void 0 ? ref : undefined;
});
var ref1;
const getCountry = createSelector(getIntl, (intl)=>{
    return (ref1 = intl === null || intl === void 0 ? void 0 : intl.country) !== null && ref1 !== void 0 ? ref1 : undefined;
});
var ref2;
const getLocale = createSelector(getIntl, (intl)=>{
    return (ref2 = intl === null || intl === void 0 ? void 0 : intl.locale) !== null && ref2 !== void 0 ? ref2 : undefined;
});
var ref3;
const getDistanceUnit = createSelector(getIntl, (intl)=>{
    return (ref3 = intl === null || intl === void 0 ? void 0 : intl.distanceUnit) !== null && ref3 !== void 0 ? ref3 : undefined;
});

var selectors = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getIntl: getIntl,
  getCurrency: getCurrency,
  getCountry: getCountry,
  getLocale: getLocale,
  getDistanceUnit: getDistanceUnit
});

var Locales;
(function(Locales) {
    Locales['en-IO'] = 'en-IO';
    Locales['ca-ES'] = 'ca-ES';
    Locales['da-DK'] = 'da-DK';
    Locales['en-GB'] = 'en-GB';
    Locales['es-ES'] = 'es-ES';
    Locales['fr-BE'] = 'fr-BE';
    Locales['it-IT'] = 'it-IT';
    Locales['nl-BE'] = 'nl-BE';
    Locales['nl-NL'] = 'nl-NL';
    Locales['pt-PT'] = 'pt-PT';
    Locales['sv-SE'] = 'sv-SE';
    Locales['en-IE'] = 'en-IE';
    Locales['fr-FR'] = 'fr-FR';
})(Locales || (Locales = {}));
var Countries;
(function(Countries) {
    Countries["BE"] = "BE";
    Countries["DK"] = "DK";
    Countries["ES"] = "ES";
    Countries["GB"] = "GB";
    Countries["IO"] = "IO";
    Countries["IT"] = "IT";
    Countries["NL"] = "NL";
    Countries["PT"] = "PT";
    Countries["SE"] = "SE";
    Countries["IE"] = "IE";
    Countries["FR"] = "FR";
})(Countries || (Countries = {}));
var Currencies;
(function(Currencies) {
    Currencies["EUR"] = "EUR";
    Currencies["GBP"] = "GBP";
    Currencies["USD"] = "USD";
    Currencies["SEK"] = "SEK";
    Currencies["DKK"] = "DKK";
})(Currencies || (Currencies = {}));

const IntlContext = /*#__PURE__*/ React__default.createContext({});
const useIntl = ()=>useContext(IntlContext)
;
function withIntl(Component) {
    const wrappedName = Component.displayName || Component.name || 'Component';
    const WithIntl = (props)=>/*#__PURE__*/ React__default.createElement(IntlContext.Consumer, null, (context)=>/*#__PURE__*/ React__default.createElement(Component, swcHelpers.extends({}, props, {
                intl: context
            }))
        )
    ;
    WithIntl.displayName = `WithIntl(${wrappedName})`;
    return WithIntl;
}

/**
 * Mapping to dynamically import the date-fns Locale object for your locale.
 * You can extend this map if you need a locale that's currently not in there if it's supported by date-fns.
 * https://date-fns.org/v2.0.0-beta.2/docs/I18n#supported-languages
 */ const dateFnsLocaleMap = {
    en: ()=>import('date-fns/locale/en-GB')
    ,
    'en-GB': ()=>import('date-fns/locale/en-GB')
    ,
    'en-US': ()=>import('date-fns/locale/en-US')
    ,
    it: ()=>import('date-fns/locale/it')
    ,
    nl: ()=>import('date-fns/locale/nl')
    ,
    pt: ()=>import('date-fns/locale/pt')
    ,
    sv: ()=>import('date-fns/locale/sv')
    ,
    da: ()=>import('date-fns/locale/da')
    ,
    fr: ()=>import('date-fns/locale/fr')
    ,
    es: ()=>import('date-fns/locale/es')
    ,
    ca: ()=>import('date-fns/locale/ca')
};
/**
 * Gets the dynamic import for the passed locale.
 * Use this function to get your locale import, don't expose the map directly.
 *
 * @example
 * const dateFnsLocaleModule = getDateFnsLocaleModule('it-IT')
 * const { default: dateFnsLocale } = await dateFnsLocaleModule()
 */ function getDateFnsLocaleModule(locale) {
    if (dateFnsLocaleMap[locale]) {
        return dateFnsLocaleMap[locale]();
    }
    // If we can't find an obj for the more specific locale (en-GB),
    // Try to find a less specific one for the language code only (en).
    const language = locale.substr(0, 2);
    if (dateFnsLocaleMap[language]) {
        return dateFnsLocaleMap[language]();
    }
    return undefined;
}

const handleFormatDate = (dateFormats, value, dateFormatKey, options)=>{
    return format(value, dateFormats[dateFormatKey], options);
};
const handleLongDate = (dateFormat, date, options)=>handleFormatDate(dateFormat, date, 'long', options)
;
const handleMidsizeDate = (dateFormat, date, options)=>handleFormatDate(dateFormat, date, 'midsize', options)
;
const handleShortDate = (dateFormat, date, options)=>handleFormatDate(dateFormat, date, 'short', options)
;
const handleFormatTime = (timeFormats, value, timeFormatKey, options)=>{
    return format(value, timeFormats[timeFormatKey || 'default'], options);
};

/**
 * Merges labels as received from the call to Bloomreach into a flat 1 level deep object,
 * and prefixes each label key with the key of the parent object (e.g. carts or stores)
 */ const mergeLabels = (labels)=>{
    return Object.keys(labels).reduce((merged, key)=>swcHelpers.objectSpread({}, merged, Object.entries(labels[key]).reduce((innerLabels, [labelKey, label])=>swcHelpers.objectSpread({}, innerLabels, {
                [`${key}.${labelKey}`]: label
            })
        , {}))
    , {});
};
const hasLabel = (labels, key)=>!!labels[key]
;
const isDebugEnabled = function() {
    try {
        return !!localStorage.getItem('gv:debug:intl');
    } catch (error) {
        return false;
    }
}();
const getLabel = (labels, key, optionsConfig = {})=>{
    const { defaultValue , hideLabelKey =false  } = optionsConfig;
    if (isDebugEnabled) {
        return `[${key}]`;
    }
    return labels[key] || defaultValue || (hideLabelKey ? '' : `[${key}]`);
};
var getFormattedLabel = (labels, key, optionsConfig = {})=>{
    const { parseLink: parseLinkEnabled , parseHTML , labelData , openLinksInNewTab  } = optionsConfig, getLabelOptions = swcHelpers.objectWithoutProperties(optionsConfig, [
        "parseLink",
        "parseHTML",
        "labelData",
        "openLinksInNewTab"
    ]);
    let label = getLabel(labels, key, getLabelOptions);
    if (isDebugEnabled) {
        return label;
    }
    if (labelData) {
        label = replaceVars(label, labelData);
    }
    if (parseLinkEnabled) {
        label = parseLink(label, openLinksInNewTab);
    }
    /*
    by default parses if parseLinkEnabled,
    unless parseHTML explicitly set to false
  */ if (parseHTML || parseLinkEnabled && parseHTML !== false) {
        label = parseHtml(label);
    }
    return label;
};
const replaceVars = (label, data)=>{
    Object.keys(data).forEach((key)=>{
        label = label.replace(`{${key}}`, data[key].toString());
    });
    return label;
};
// Parses MarkDown style links to HTML string
// [I'm an inline-style link with title](https://www.google.com "Google's Homepage")
const parseLink = (label, openInNewTab = false)=>{
    const regex = /[\[]{1}([^\]\[]+)[\]]{1}[(]{1}([^()"]+)("(.+)")?[)]{1}/g;
    return label.replace(regex, openInNewTab ? '<a href="$2" title="$4" target="_blank" rel="noopener">$1</a>' : '<a href="$2" title="$4">$1</a>');
};

const supportedLocales = Object.keys(Locales);
const supportedCurrencies = Object.keys(Currencies);
const supportedDistanceUnits = Object.keys(DistanceUnit);
const settingsDefaults = {
    distanceUnit: DistanceUnit.km,
    dateFormat: {
        long: 'EEEE do MMMM, yyyy',
        midsize: 'dd MMMM yyyy',
        short: 'dd/MM/yyyy'
    },
    timeFormat: {
        default: 'H:mm'
    }
};
const mergeSettings = (settings)=>swcHelpers.objectSpread({}, settingsDefaults, settings)
;
const IntlProvider = ({ settings , labels , children  })=>{
    const { country , currency , locale , distanceUnit , dateFormat , timeFormat  } = mergeSettings(settings);
    const dispatch = useDispatch();
    const [dateFnsLocale, setDateFnsLocale] = React.useState();
    const value = React.useMemo(()=>({
            country,
            currency,
            distanceUnit,
            locale,
            dateFormat,
            timeFormat,
            dateFnsLocale,
            dateTime: makeDateTimeObject(dateFnsLocale, dateFormat, timeFormat),
            getLabel: (key, options)=>getLabel(labels, key, options)
            ,
            getFormattedLabel: (key, options)=>getFormattedLabel(labels, key, options)
            ,
            hasLabel: (key)=>hasLabel(labels, key)
        })
    , [
        country,
        currency,
        dateFnsLocale,
        dateFormat,
        distanceUnit,
        labels,
        locale,
        timeFormat
    ]);
    React.useEffect(()=>{
        // Log some warnings if invalid settings were passed
        validateSettings({
            country,
            currency,
            locale,
            distanceUnit
        });
        // Init/update intl settings to our redux state
        dispatch(setConfig({
            country,
            currency,
            locale,
            distanceUnit,
            dateFormat,
            timeFormat
        }));
        // Lazy load the date-fns locale module for the locale that was passed
        const dateFnsLocaleModule = getDateFnsLocaleModule(locale);
        if (dateFnsLocaleModule) {
            dateFnsLocaleModule.then((module)=>{
                if (module && 'default' in module) {
                    setDateFnsLocale(module.default);
                }
            });
        } else {
            console.warn(`Could not find a date-fns locale obj for locale: ${locale}`);
        }
    }, [
        country,
        currency,
        dateFormat,
        dispatch,
        distanceUnit,
        locale,
        timeFormat
    ]);
    return /*#__PURE__*/ React.createElement(IntlContext.Provider, {
        value: value
    }, children);
};
function makeDateTimeObject(locale, dateFormat, timeFormat) {
    const options = !!locale ? {
        locale
    } : undefined;
    return {
        formatDate: (dateTime, key)=>{
            return handleFormatDate(dateFormat, dateTime, key, options);
        },
        formatLongDate: (dateTime)=>{
            return handleLongDate(dateFormat, dateTime, options);
        },
        formatMidsizeDate: (dateTime)=>{
            return handleMidsizeDate(dateFormat, dateTime, options);
        },
        formatShortDate: (dateTime)=>{
            return handleShortDate(dateFormat, dateTime, options);
        },
        formatTime: (dateTime, key)=>{
            return handleFormatTime(timeFormat, dateTime, key, options);
        }
    };
}
function validateSettings({ country , currency , locale , distanceUnit  }) {
    if (!(country in Countries)) {
        console.warn(`Invalid country "${country}" set on IntlProvider. Use 2 uppercase characters like "GB"`);
    }
    if (!(locale in Locales)) {
        console.warn(`Unsupported locale "${locale}" set on IntlProvider. Supported locales are: ${supportedLocales.join(', ')}`);
    }
    if (!supportedCurrencies.includes(currency)) {
        console.warn(`Unsupported or invalid currency "${currency}" set on IntlProvider. Supported currencies are: ${supportedCurrencies.join(', ')}`);
    }
    if (!distanceUnit || !supportedDistanceUnits.includes(distanceUnit)) {
        console.warn(`Unsupported or invalid distance unit "${distanceUnit}" set on IntlProvider. Supported distance units are: ${supportedDistanceUnits.join(', ')}`);
    }
}

ReducersRegistry.register('intl', reducer$1);

/**
 * Statuses
 */ const UNAVAILABLE = 'unavailable';
const IDLE = 'idle';
const UNINIT = 'uninitialized';
const SUCCESS = 'success';
const LOADING = 'loading';
const FAILED = 'failed';
const WARNING = 'warning';
/**
 * State creators
 */ const unavailableState = ()=>({
        status: UNAVAILABLE
    })
;
const idleState = ()=>({
        status: IDLE
    })
;
const uninitState = ()=>({
        status: UNINIT
    })
;
const loadingState = ()=>({
        status: LOADING
    })
;
const successState = (result)=>({
        status: SUCCESS,
        result
    })
;
const warningState = (state, error)=>{
    return swcHelpers.objectSpread({}, state, {
        status: WARNING,
        error
    });
};
const failedState = (error)=>({
        status: FAILED,
        error
    })
;
/**
 * State guards
 */ const statusIdle = (state)=>state.status === IDLE
;
const statusUnavailable = (state)=>state.status === UNAVAILABLE
;
const statusUninit = (state)=>state.status === UNINIT
;
const statusLoading = (state)=>state.status === LOADING
;
const statusSuccess = (state)=>state.status === SUCCESS
;
const statusWarning = (state)=>state.status === WARNING
;
const statusFailed = (state)=>state.status === FAILED
;

var ViewHistoryKey;
(function(ViewHistoryKey) {
    ViewHistoryKey["Products"] = 'view_history.products';
})(ViewHistoryKey || (ViewHistoryKey = {}));

// Re-export within redux namespace
const ReduxProvider = Provider;
const ReduxPersistGate = PersistGate;
const reducer = actions.reducer;

export { ApiConfigContext, ApiConfig as ApiConfigProvider, selectors$2 as ApiConfigSelectors, ClickAndReserve as ClickAndReserveFeatureConfigEnum, Countries, Currencies, DEFAULT_LIMIT, FAILED, FeatureToggleProvider, selectors$1 as FeatureToggleSelectors, IDLE, IntlContext, IntlProvider, selectors as IntlSelectors, LOADING, Locales, ReducersRegistry, ReduxPersistGate, ReduxProvider, SUCCESS, SagaRegistry, UNAVAILABLE, UNINIT, ViewHistoryKey, WARNING, assertApiConfig, createFeatureConfigs, createFeatureSet, failedState, idleState, initStore, loadingState, mergeLabels, parseLink, persistConfig, persistReducer, reducer, statusFailed, statusIdle, statusLoading, statusSuccess, statusUnavailable, statusUninit, statusWarning, successState, unavailableState, uninitState, useApiConfig, useClickAndReserveFeatureConfig, useFeatureToggles, useIntl, useSaga, useStore, useViewHistory, warningState, withApiConfig, withFeatureToggles, withIntl };
//# sourceMappingURL=index.js.map
